import {
  DecorationElement,
  MapElement,
  Size,
  SpotLayout,
} from '@spiaggeit/map-canvas'
import {
  Color,
  DecorationType,
  ElementType,
  SpotType,
  isSpotSubType,
} from '@spiaggeit/spit-core'

import { MapElementWithSectorId, MapResponseElement } from '../models/map'

import { MAP_CELL_SIZE } from './constants'

export enum MapDecorationOrientation {
  NONE = '',
  VERTICAL = 'vertical',
  HORIZONTAL = 'horizontal',
}

const LINE_HEIGHT = 2
const OLD_LINES_VERTICAL_ADJUST = 0.5 // half a row

function getDecorationSubtype(element: MapResponseElement): DecorationType {
  if (element.t === 'footboard') {
    return DecorationType.FOOTBOARD
  }
  if (element.t === 'mapLabel') {
    return DecorationType.LABEL
  }
  return element.et as DecorationType
}

function getCellSize(element: MapResponseElement): Size {
  if (element.et === DecorationType.BALLOON) {
    return {
      height: element.eh ?? 0,
      width: element.ew ?? 0,
    }
  }

  return typeof element.eh === 'number' && typeof element.ew === 'number'
    ? {
        height: element.eh * MAP_CELL_SIZE,
        width: element.ew * MAP_CELL_SIZE,
      }
    : { height: MAP_CELL_SIZE, width: MAP_CELL_SIZE }
}

function toDecoration(element: MapResponseElement): DecorationElement {
  if (!element.sid) {
    throw new Error('Cannot convert a decoration without an identifier')
  }

  const cellSize = getCellSize(element)

  if (element.t === 'mapLineV' || element.t === 'mapLineH') {
    const isVertical = element.t === 'mapLineV'

    const x = element.pX * MAP_CELL_SIZE

    // adjust horizontal lines y position
    const verticalAdjust = isVertical ? 0 : OLD_LINES_VERTICAL_ADJUST
    const y = (element.pY + verticalAdjust) * MAP_CELL_SIZE
    const width = MAP_CELL_SIZE

    return {
      cellSize,
      height: LINE_HEIGHT,
      id: String(element.sid),
      name: element.bgc,
      rotation: isVertical ? 90 : 0,
      subType: DecorationType.LINE,
      type: ElementType.DECORATION,
      url: element.url,
      width,
      x: isVertical ? x + MAP_CELL_SIZE / 2 : x,
      y,
      zIndex: element.zi,
    }
  }

  const isLine = element.et === DecorationType.LINE
  const isBalloon = element.et === DecorationType.BALLOON
  const isMapLabel = element.t === 'mapLabel'

  return {
    cellSize,
    height: isLine ? LINE_HEIGHT : cellSize.height,
    id: String(element.sid),
    name: isMapLabel ? element.mlt ?? '' : element.n,
    rotation: element.er,
    subType: getDecorationSubtype(element),
    type: ElementType.DECORATION,
    url: element.url,
    width: cellSize.width,
    x: element.pX * MAP_CELL_SIZE,
    y: element.pY * MAP_CELL_SIZE,
    zIndex: element.zi,
    ...(isBalloon && element.ed ? { direction: element.ed } : {}),
  }
}

function getSpotIconColor(
  element: MapResponseElement,
  unavailableSectors: number[]
): Color {
  if (unavailableSectors.includes(element.s)) {
    return Color.GREY
  }
  return element.a ? Color.GREEN : Color.GREY
}

function toSpot(
  element: MapResponseElement,
  unavailableSectors: number[]
): MapElementWithSectorId {
  return {
    ...element,
    height: MAP_CELL_SIZE,
    id: String(element.sid),
    layout: SpotLayout.BOOKING_NAME,
    name: element.t === SpotType.PLAYFIELD ? `${element.n} ` : element.n,
    sectorId: element.s,
    staging: null,
    style: {
      iconColor: getSpotIconColor(element, unavailableSectors),
    },
    subType: element.t as SpotType,
    type: ElementType.SPOT as ElementType.SPOT,
    width: MAP_CELL_SIZE,
    x: element.pX * MAP_CELL_SIZE,
    y: element.pY * MAP_CELL_SIZE,
  }
}

export const getFormattedMapElement = (
  element: MapResponseElement,
  unavailableSectors: number[]
): MapElement => {
  if (isSpotSubType(element.t)) {
    return toSpot(element, unavailableSectors)
  } else {
    return toDecoration(element)
  }
}
